import { useEffect, useState } from "react";
import { Room, RoomEvent } from "livekit-client";

/**
 * Hook to listen for Langfuse session_id from the agent
 * 
 * The agent sends the session_id via LiveKit data channel when a session starts.
 * This session_id is used later to submit user feedback to Langfuse.
 * 
 * @param room - The LiveKit room instance
 * @returns session_id string or null if not yet received
 */
export function useLangfuseSession(room: Room | undefined) {
    const [sessionId, setSessionId] = useState<string | null>(null);

    useEffect(() => {
        console.log('🔌 [useLangfuseSession] Room:', room ? 'Connected' : 'Not available');

        if (!room) {
            console.warn('⚠️ [useLangfuseSession] Room is undefined, cannot listen for session_id');
            return;
        }

        // Use simplified signature matching LiveKit v2 API
        const handleData = (payload: Uint8Array, _participant?: any, _kind?: any, topic?: string) => {
            console.log('📨 [useLangfuseSession] Data received:', { topic, payloadSize: payload.length });

            // Listen for "langfuse_session" topic from agent
            if (topic === "langfuse_session") {
                console.log('🎯 [useLangfuseSession] Matched langfuse_session topic!');
                try {
                    const data = JSON.parse(new TextDecoder().decode(payload));
                    console.log('📦 [useLangfuseSession] Parsed data:', data);
                    if (data.session_id) {
                        setSessionId(data.session_id);
                        console.log("✅ [useLangfuseSession] Received Langfuse session_id:", data.session_id);
                    } else {
                        console.warn('⚠️ [useLangfuseSession] Data has no session_id field');
                    }
                } catch (error) {
                    console.error("❌ [useLangfuseSession] Failed to parse session_id data:", error);
                }
            }
        };

        console.log('👂 [useLangfuseSession] Registering DataReceived listener');
        room.on(RoomEvent.DataReceived, handleData as any);

        return () => {
            console.log('🔇 [useLangfuseSession] Unregistering DataReceived listener');
            room.off(RoomEvent.DataReceived, handleData as any);
        };
    }, [room]);

    return sessionId;
}
